import Vue from 'vue'
import Vuex from 'vuex'
import router from '@/router'
import * as jwt from '@/services/auth'
import { getLandingPath } from '@/utils/landing'
import { getApiErrorMessage } from '@/utils/apiError'
import clearAllToasts from '@/utils/clearToasts'

const Auth = {
  login: jwt.login,
  register: jwt.register,
  verifyEmailCode: jwt.verifyEmailCode,
  verifyTotp: jwt.verifyTotp,
  verifyToken: jwt.verifyToken,
  resendCode: jwt.resendCode,
  resetPassword: jwt.resetPassword,
  currentAccount: jwt.currentAccount,
  forgotPassword: jwt.forgotPassword,
  logout: jwt.logout,
}
Vue.use(Vuex)

export default {
  namespaced: true,
  state: {
    id: '',
    name: '',
    first_name: '',
    last_name: '',
    user: null,
    role: '',
    roleSlug: '',
    email: '',
    phone: '',
    avatar: '',
    /** From GET /users/me — { id, color, ... } for profile avatar tint */
    userColor: null,
    authorized: false,
    loading: false,
    userId: '',
    /** From /auth/signin for a 2FA account; the 2FA page sends it with the code. */
    loginToken: '',
    organization: null,
  },
  mutations: {
    SET_STATE(state, payload) {
      Object.assign(state, {
        ...payload,
      })
    },
  },
  actions: {
    LOGIN({ commit, dispatch, state }, { payload }) {
      commit('SET_STATE', {
        loading: true,
      })
      const login = Auth.login
      login(payload).then((response) => {
        if (!response) {
          Vue.prototype.$notification.warning({
            message: 'Login failed',
            description: 'Invalid user name or password.',
          })
          commit('SET_STATE', {
            loading: false,
          })
          return
        }
        const { success, userId, accessToken, needs2FA, loginToken } = response
        if (success && needs2FA) {
          // Two-factor flow — backend already emailed the 6-digit code. Pause
          // here and let the 2FA page submit it via VERIFY_OTP.
          commit('SET_STATE', {
            loading: false,
            userId,
            loginToken: loginToken || '',
          })
          Vue.prototype.$notification.info({
            message: 'Two-Factor required',
            description:
              'Open your authenticator app and enter the 6-digit code.',
          })
          router.push('/auth/2FA')
          return
        }
        if (success) {
          Vue.prototype.$notification.success({
            message: 'Logged In',
            description: 'You have successfully logged in to Routr.',
          })
          localStorage.setItem('accessToken', accessToken)
          commit('SET_STATE', {
            loading: false,
            authorized: true,
          })
          // Await LOAD_CURRENT_ACCOUNT so roleSlug is populated before
          // we decide the landing path (guardian → parent portal).
          dispatch('LOAD_CURRENT_ACCOUNT').then(() => {
            const redirect =
              router.currentRoute.query.redirect ||
              getLandingPath(state.roleSlug)
            router.push(redirect)
          })
        } else {
          Vue.prototype.$notification.warning({
            message: 'Login failed',
            description: 'Invalid user name or password.',
          })
          commit('SET_STATE', {
            loading: false,
          })
        }
      })
    },
    RESET_PASSWORD({ commit, state }, { password }) {
      commit('SET_STATE', {
        loading: true,
      })
      const userId = state.userId
      const resetPassword = Auth.resetPassword
      resetPassword({ userId, password })
        .then((res) => {
          if (res.data.success) {
            Vue.prototype.$notification.success({
              message: 'Success',
              description: 'You have successfully changed the password',
            })
            commit('SET_STATE', {
              loading: false,
            })
            router.push('/auth/login')
          }
        })
        .catch((error) => {
          const errorMessage = getApiErrorMessage(error)
          console.log(errorMessage)
        })
        .finally(() =>
          commit('SET_STATE', {
            loading: false,
          }),
        )
    },
    REGISTER({ commit }, { payload }) {
      commit('SET_STATE', {
        loading: true,
      })
      const register = Auth.register
      register(payload)
        .then((res) => {
          if (res.data.success) {
            Vue.prototype.$notification.success({
              message: 'Success',
              description: 'You are successfully registered.',
            })
            commit('SET_STATE', {
              loading: false,
            })
            router.push('/auth/login')
          }
        })
        .catch((error) => {
          // Raised on /auth/register while signed out, so these must not go
          // through notification/NEW - that path drops toasts for a session
          // that no longer exists. Matches the success toast above.
          //
          // Test the status rather than the body: this used to compare
          // data.error against 'Conflict', which meant every other failure
          // (500, offline, validation) showed the user nothing at all.
          if (error && error.response && error.response.status === 409) {
            Vue.prototype.$notification.warning({
              message: 'Get Error',
              description: 'Your email is already signed up',
            })
            return
          }
          Vue.prototype.$notification.warning({
            message: 'Registration failed',
            description: getApiErrorMessage(error),
          })
        })
        .finally(() =>
          commit('SET_STATE', {
            loading: false,
          }),
        )
    },
    LOAD_CURRENT_ACCOUNT({ commit, dispatch }) {
      commit('SET_STATE', {
        loading: false,
      })
      const currentAccount = Auth.currentAccount

      // Return the promise so callers can `await` this action and know
      // user.roleSlug is populated (needed for role-aware redirects).
      return currentAccount().then((response) => {
        if (response) {
          localStorage.setItem('timeZone', response.organization.timeZone)
          localStorage.setItem('organizationId', response.organization.id)
          localStorage.setItem('userId', response.id)
          const {
            id,
            email,
            firstName: first_name,
            lastName: last_name,
            role,
            avatar,
            phone,
            organization,
            userColor,
          } = response
          // role from API is { id, role: "Admin", slug: "admin", hierarchy }.
          // Older code paths read state.role as a display string, so keep that
          // shape. Permission decisions use roleSlug.
          const roleName = (role && (role.role || role.name)) || ''
          const roleSlug = (role && role.slug) || ''
          commit('SET_STATE', {
            id,
            first_name,
            last_name,
            email,
            avatar,
            role: roleName,
            roleSlug,
            phone,
            organization,
            userColor: userColor || null,
            loading: false,
            authorized: true,
          })
          if (id) {
            dispatch('permissions/FETCH_PERMISSIONS', { userId: id, force: true }, { root: true })
          }
        } else {
          commit('SET_STATE', {
            loading: false,
            authorized: false,
          })
          const routeRequiresAuth = router.currentRoute.matched.some(
            (record) => record.meta.authRequired,
          )
          const currentPath = router.currentRoute.fullPath
          const isPublicAuthPage =
            currentPath.includes('/forgot-password-submit') ||
            currentPath.includes('/accept-invite')
          if (routeRequiresAuth && !isPublicAuthPage) {
            router.push('/auth/login')
          }
        }
      })
    },
    VERIFY_FORGOT_PASSWORD_TOKEN({ commit, dispatch }, { payload }) {
      const token = payload
      const verifyToken = Auth.verifyToken
      if (token) {
        verifyToken(token).then((response) => {
          const { success, accessToken, userId } = response.data
          if (success) {
            localStorage.setItem('accessToken', accessToken)
            router.push({
              path: '/auth/forgot-password-submit',
            })
            commit('SET_STATE', {
              loading: false,
              userId: userId,
            })
          } else {
            Vue.prototype.$notification.warning({
              message: 'Failed',
              description: 'Session has been expired. Please try again.',
            })
            router.push('/auth/forgot-password')
          }
        })
      } else {
        Vue.prototype.$notification.warning({
          message: 'Failed',
          description: 'Token is invalid.',
        })
        router.push('/auth/forgot-password')
      }
    },
    LOGOUT({ commit, dispatch }) {
      localStorage.removeItem('timeZone')
      const logout = Auth.logout
      logout().then(async () => {
        commit('SET_STATE', {
          id: '',
          first_name: '',
          last_name: '',
          name: '',
          role: '',
          roleSlug: '',
          email: '',
          avatar: '',
          userColor: null,
          organization: null,
          authorized: false,
          loading: false,
          payload: null,
        })
        dispatch('permissions/CLEAR_PERMISSIONS', null, { root: true })
        // Nothing from the session follows the user onto the login page.
        clearAllToasts()
        router.push('/auth/login')
      })
    },
    RESEND_CODE({ state }) {
      const userId = state.userId
      const resendCode = Auth.resendCode
      resendCode({ userId }).then((success) => {
        if (success) {
          Vue.prototype.$notification.success({
            message: 'Success',
            description: 'Verification code has been sent again.',
          })
        } else {
          Vue.prototype.$notification.warning({
            message: 'Failed',
            description: 'Session has been removed. Please sign in again.',
          })
          router.push('/auth/login')
        }
      })
    },
    VERIFY_TOTP({ commit, state, dispatch }, { payload }) {
      const { code, trustDevice } = payload
      const { userId, loginToken } = state
      const verifyTotp = Auth.verifyTotp
      verifyTotp({ code, userId, loginToken, trustDevice }).then((response) => {
        if (!response || !response.success) {
          Vue.prototype.$notification.warning({
            message: 'Failed',
            description: 'Invalid authentication code.',
          })
          commit('SET_STATE', { loading: false })
          return
        }
        localStorage.setItem('accessToken', response.accessToken)
        Vue.prototype.$notification.success({
          message: 'Logged In',
          description: 'You have successfully logged in to Routr.',
        })
        commit('SET_STATE', { loading: false, authorized: true })
        dispatch('LOAD_CURRENT_ACCOUNT')
        router.push('/tools/contracts/all')
      })
    },
    VERIFY_OTP({ commit, state, dispatch }, { payload }) {
      const { code, trustDevice } = payload
      const userId = state.userId

      const verifyEmailCode = Auth.verifyEmailCode

      verifyEmailCode({ code, userId, trustDevice }).then((response) => {
        if (!response) {
          Vue.prototype.$notification.warning({
            message: 'Failed',
            description: 'Invalid verification code.',
          })
          commit('SET_STATE', {
            loading: false,
          })
          return
        }
        const { success, accessToken } = response
        localStorage.setItem('accessToken', accessToken)
        if (success) {
          Vue.prototype.$notification.success({
            message: 'Logged In',
            description: 'You have successfully logged in to Routr.',
          })
          commit('SET_STATE', {
            loading: false,
            authorized: true,
          })
          dispatch('LOAD_CURRENT_ACCOUNT').then(() => {
            const redirect =
              router.currentRoute.query.redirect ||
              getLandingPath(state.roleSlug)
            router.push(redirect)
          })
        } else {
          Vue.prototype.$notification.warning({
            message: 'Failed',
            description: 'Invalid verification code.',
          })
          commit('SET_STATE', {
            loading: false,
          })
        }
      })
    },
    FORGOT_PASSWORD({ commit, rootState }, { payload }) {
      const { email } = payload
      const forgotPassword = Auth.forgotPassword
      forgotPassword(email).then((success) => {
        if (success) {
          Vue.prototype.$notification.success({
            message: 'Password reset link sent',
            description: 'Please check your email for the password reset code.',
          })
          commit('SET_STATE', {
            loading: false,
            authorized: false,
          })
          router.push({
            path: '/auth/forgot-password-resend',
            query: { email: payload.email },
          })
        }
        if (!success) {
          commit('SET_STATE', {
            loading: false,
          })
        }
      })
    },
  },
  getters: {
    user: (state) => state,
  },
}
